import { useEffect, useState, useCallback } from "react";
import {
  listJobs, createJob, updateJob, deleteJob, updateJobStatus, setJobMaterials,
  uploadJobPhoto, deleteJobPhoto, jobPhotoUrl, getJob, jobStockOrders,
  listFieldEmployees, listTeams, apiError,
} from "@/lib/api";
import { Card, ConfirmDelete, Loading } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Wrench, Truck, MapPin, PlayCircle, CheckCircle2, Camera, Package, Clock, ChevronRight } from "lucide-react";

const JOB_STATUS = {
  assigned: { label: "Assigned", cls: "bg-slate-100 text-slate-600" },
  en_route: { label: "En Route", cls: "bg-blue-100 text-blue-700" },
  on_site: { label: "On Site", cls: "bg-indigo-100 text-indigo-700" },
  in_progress: { label: "In Progress", cls: "bg-amber-100 text-amber-700" },
  completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-700" },
  cancelled: { label: "Cancelled", cls: "bg-rose-100 text-rose-700" },
};
const NEXT_ACTION = {
  assigned: { to: "en_route", label: "Start travelling", icon: Truck },
  en_route: { to: "on_site", label: "Arrived on site", icon: MapPin },
  on_site: { to: "in_progress", label: "Start work", icon: PlayCircle },
  in_progress: { to: "completed", label: "Complete job", icon: CheckCircle2 },
};

function Badge({ status }) {
  const m = JOB_STATUS[status] || JOB_STATUS.assigned;
  return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${m.cls}`} data-testid={`job-status-${status}`}>{m.label}</span>;
}

const emptyForm = () => ({ title: "", description: "", site_address: "", priority: "normal", scheduled_date: "", assigned_team_id: "none", assigned_to_id: "none", work_report_number: "", quoted_amount: "" });

export function Jobs({ manage = false, accentClass = "bg-[#1E7A6B] hover:bg-[#186052]" }) {
  const [jobs, setJobs] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(() => {
    listJobs().then(setJobs).catch((e) => { setJobs([]); toast.error(apiError(e)); });
  }, []);
  useEffect(() => {
    load();
    if (manage) {
      listFieldEmployees().then((d) => setEmployees(d.employees || [])).catch(() => {});
      listTeams().then(setTeams).catch(() => {});
    }
  }, [load, manage]);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (j) => {
    setEditing(j);
    setForm({
      title: j.title || "", description: j.description || "", site_address: j.site_address || "",
      priority: j.priority || "normal", scheduled_date: (j.scheduled_date || "").slice(0, 10),
      assigned_team_id: j.assigned_team_id || "none", assigned_to_id: j.assigned_to_id || "none",
      work_report_number: j.work_report_number || "", quoted_amount: j.quoted_amount ?? "",
    });
    setOpen(true);
  };
  const save = async () => {
    if (!form.title.trim()) return toast.error("Job title is required");
    setSaving(true);
    try {
      const body = {
        ...form,
        assigned_team_id: form.assigned_team_id === "none" ? null : form.assigned_team_id,
        assigned_to_id: form.assigned_to_id === "none" ? null : form.assigned_to_id,
        scheduled_date: form.scheduled_date || null,
        quoted_amount: form.quoted_amount === "" ? null : Number(form.quoted_amount),
      };
      if (editing) await updateJob(editing.id, body); else await createJob(body);
      toast.success(editing ? "Job updated" : "Job created & assigned");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!jobs) return <Loading />;

  return (
    <div data-testid="jobs">
      <div className="flex justify-between items-center mb-4 gap-3">
        <p className="text-sm text-slate-500 hidden sm:block">{manage ? "Create jobs and assign them to teams or field employees." : "Your assigned jobs. Tap a job to update its progress."}</p>
        {manage && <Button onClick={openNew} className={`text-white ${accentClass}`} data-testid="new-job-btn"><Plus className="w-4 h-4 mr-2" /> New Job</Button>}
      </div>

      {jobs.length === 0 ? (
        <Card className="p-10 text-center text-slate-400" data-testid="jobs-empty">
          <Wrench className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          {manage ? "No jobs yet. Create your first job." : "No jobs assigned to you yet."}
        </Card>
      ) : (
        <div className="space-y-3" data-testid="job-list">
          {jobs.map((j) => (
            <Card key={j.id} className="p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setDetailId(j.id)} data-testid={`job-${j.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-[#0B132B]">{j.number}</span>
                    <Badge status={j.status} />
                    {j.priority === "high" && <span className="text-xs text-rose-600 font-semibold">High</span>}
                  </div>
                  <div className="text-sm text-slate-700 mt-1 font-medium truncate">{j.title}</div>
                  <div className="text-xs text-slate-400 mt-0.5 truncate">
                    {j.site_address || "—"}{j.assigned_to_name ? ` · ${j.assigned_to_name}` : ""}{j.assigned_team_name ? ` · ${j.assigned_team_name}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {manage && (
                    <>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(j); }} data-testid={`job-edit-${j.id}`}><Wrench className="w-4 h-4" /></Button>
                      <span onClick={(e) => e.stopPropagation()}>
                        <ConfirmDelete onConfirm={async () => { await deleteJob(j.id); load(); toast.success("Job deleted"); }} title="Delete job?" trigger={<Button variant="ghost" size="sm" className="text-rose-600" data-testid={`job-delete-${j.id}`}><Trash2 className="w-4 h-4" /></Button>} />
                      </span>
                    </>
                  )}
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create / edit (manage) */}
      {manage && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="job-dialog">
            <DialogHeader><DialogTitle>{editing ? `Edit ${editing.number}` : "New Job"}</DialogTitle><DialogDescription>Assign the job to a team and/or a field employee.</DialogDescription></DialogHeader>
            <div className="space-y-3 py-1">
              <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Fix burst geyser" data-testid="job-title" /></div>
              <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div><Label>Site address</Label><Input value={form.site_address} onChange={(e) => setForm({ ...form, site_address: e.target.value })} data-testid="job-site" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="job-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>{["low", "normal", "high"].map((p) => <SelectItem key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Scheduled date</Label><Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Team</Label>
                  <Select value={form.assigned_team_id} onValueChange={(v) => setForm({ ...form, assigned_team_id: v })}>
                    <SelectTrigger data-testid="job-team"><SelectValue placeholder="No team" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">No team</SelectItem>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Field employee</Label>
                  <Select value={form.assigned_to_id} onValueChange={(v) => setForm({ ...form, assigned_to_id: v })}>
                    <SelectTrigger data-testid="job-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Unassigned</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Work report reference (optional)</Label><Input value={form.work_report_number} onChange={(e) => setForm({ ...form, work_report_number: e.target.value })} placeholder="RPT-0001" /></div>
              <div><Label>Quoted amount (revenue, optional)</Label><Input type="number" value={form.quoted_amount} onChange={(e) => setForm({ ...form, quoted_amount: e.target.value })} placeholder="e.g. 4500.00" data-testid="job-quoted-amount" /><p className="text-xs text-slate-400 mt-1">Used for margin only when all material costs are recorded.</p></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} className={`text-white ${accentClass}`} data-testid="save-job-btn">{saving ? "Saving…" : editing ? "Save" : "Create job"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <JobDetail jobId={detailId} onClose={() => setDetailId(null)} onChanged={load} canEdit={true} accentClass={accentClass} />
    </div>
  );
}

function JobDetail({ jobId, onClose, onChanged, canEdit, accentClass }) {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [workText, setWorkText] = useState("");
  const [mats, setMats] = useState([]);
  const [stock, setStock] = useState([]);

  const reload = useCallback(() => {
    if (!jobId) return;
    getJob(jobId).then((j) => { setJob(j); setMats(j.materials_used || []); }).catch((e) => toast.error(apiError(e)));
    jobStockOrders(jobId).then(setStock).catch(() => {});
  }, [jobId]);
  useEffect(() => { if (jobId) reload(); else setJob(null); }, [jobId, reload]);

  if (!jobId) return null;

  const advance = async (to) => {
    if (to === "completed") { setWorkText(job.work_performed || ""); setCompleteOpen(true); return; }
    setBusy(true);
    try { await updateJobStatus(jobId, { status: to }); toast.success(`Marked ${JOB_STATUS[to].label}`); reload(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const complete = async () => {
    setBusy(true);
    try { await updateJobStatus(jobId, { status: "completed", work_performed: workText }); toast.success("Job completed"); setCompleteOpen(false); reload(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const saveMats = async (next) => {
    setMats(next);
    try {
      const clean = next.filter((m) => (m.description || "").trim()).map((m) => ({
        ...m, quantity: Number(m.quantity) || 0,
        unit_cost: m.unit_cost === "" || m.unit_cost == null ? null : Number(m.unit_cost),
      }));
      const updated = await setJobMaterials(jobId, clean);
      setJob(updated); onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const addMat = () => saveMats([...mats, { description: "", quantity: 1, unit: "", code: "", unit_cost: "" }]);
  const setMat = (i, k, v) => setMats(mats.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)));
  const commitMat = () => saveMats(mats);
  const rmMat = (i) => saveMats(mats.filter((_, idx) => idx !== i));

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { await uploadJobPhoto(jobId, file, ""); toast.success("Photo added"); reload(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); e.target.value = ""; }
  };

  const next = job && NEXT_ACTION[job.status];

  return (
    <>
      <Dialog open={!!jobId} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="job-detail-dialog">
          {!job ? <><DialogHeader><DialogTitle>Loading job…</DialogTitle></DialogHeader><Loading /></> : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">{job.number} <Badge status={job.status} /></DialogTitle>
                <DialogDescription>{job.title}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-1">
                {job.site_address && <div className="text-sm text-slate-600"><MapPin className="w-4 h-4 inline mr-1 text-slate-400" />{job.site_address}</div>}
                {job.description && <p className="text-sm text-slate-500">{job.description}</p>}

                {next && job.status !== "completed" && job.status !== "cancelled" && (
                  <Button onClick={() => advance(next.to)} disabled={busy} className={`w-full h-12 text-white ${accentClass}`} data-testid="job-advance-btn">
                    <next.icon className="w-5 h-5 mr-2" /> {next.label}
                  </Button>
                )}

                {/* Materials */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="flex items-center gap-1"><Package className="w-4 h-4" /> Materials used</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addMat} data-testid="job-add-material"><Plus className="w-3.5 h-3.5" /></Button>
                  </div>
                  <div className="space-y-2">
                    {mats.length === 0 && <p className="text-xs text-slate-400">None recorded.</p>}
                    {mats.map((m, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2" data-testid={`job-material-${i}`}>
                        <Input className="col-span-4" value={m.description} onChange={(e) => setMat(i, "description", e.target.value)} onBlur={commitMat} placeholder="Material" data-testid={`job-material-desc-${i}`} />
                        <Input className="col-span-2" type="number" value={m.quantity} onChange={(e) => setMat(i, "quantity", e.target.value)} onBlur={commitMat} placeholder="Qty" />
                        <Input className="col-span-3" type="number" value={m.unit_cost ?? ""} onChange={(e) => setMat(i, "unit_cost", e.target.value)} onBlur={commitMat} placeholder="Unit cost" data-testid={`job-material-cost-${i}`} />
                        <Input className="col-span-2" value={m.unit} onChange={(e) => setMat(i, "unit", e.target.value)} onBlur={commitMat} placeholder="Unit" />
                        <Button type="button" variant="ghost" size="sm" className="col-span-1 text-rose-600" onClick={() => rmMat(i)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    ))}
                  </div>
                  {job.cost && (
                    <div className="mt-2 text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid="job-cost-summary">
                      <div className="flex justify-between"><span className="text-slate-500">Materials cost</span><span className="font-semibold text-[#0B132B]">R {(job.cost.materials_cost || 0).toFixed(2)}{!job.cost.cost_complete && job.cost.materials_total > 0 ? "*" : ""}</span></div>
                      {job.cost.quoted_amount != null && <div className="flex justify-between"><span className="text-slate-500">Quoted</span><span className="text-slate-700">R {Number(job.cost.quoted_amount).toFixed(2)}</span></div>}
                      {job.cost.margin_available ? (
                        <div className="flex justify-between"><span className="text-slate-500">Margin</span><span className={`font-semibold ${job.cost.margin >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="job-margin">R {job.cost.margin.toFixed(2)}</span></div>
                      ) : (
                        <div className="text-xs text-amber-600 mt-1" data-testid="job-margin-unavailable">Margin unavailable — {job.cost.reason}</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Photos */}
                <div>
                  <Label className="flex items-center gap-1 mb-1"><Camera className="w-4 h-4" /> Photos</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {(job.photos || []).map((p) => (
                      <div key={p.id} className="relative group">
                        <img src={jobPhotoUrl(job.id, p.id)} alt={p.caption} className="w-full h-20 object-cover rounded-lg border border-slate-200" data-testid={`job-photo-${p.id}`} />
                        <button onClick={async () => { await deleteJobPhoto(job.id, p.id); reload(); }} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 text-rose-600 opacity-0 group-hover:opacity-100"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                    <label className="w-full h-20 border-2 border-dashed border-slate-200 rounded-lg flex flex-col items-center justify-center text-slate-400 cursor-pointer hover:border-slate-300" data-testid="job-photo-add">
                      <Camera className="w-5 h-5" /><span className="text-xs mt-1">Add</span>
                      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid="job-photo-input" />
                    </label>
                  </div>
                </div>

                {job.work_performed && (
                  <div><Label>Work performed</Label><p className="text-sm text-slate-600 mt-1">{job.work_performed}</p></div>
                )}

                {/* Linked stock orders */}
                <div data-testid="job-linked-stock">
                  <Label className="flex items-center gap-1 mb-1"><Package className="w-4 h-4" /> Linked stock orders</Label>
                  {stock.length === 0 ? <p className="text-xs text-slate-400">None linked. Link one from the Stock Orders screen.</p> : (
                    <div className="space-y-1.5">
                      {stock.map((so) => (
                        <div key={so.id} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid={`job-stock-${so.id}`}>
                          <span className="font-medium text-[#0B132B]">{so.number}</span>
                          <span className="text-xs text-slate-500">{(so.supplier_snapshot || {}).name || (so.branch_snapshot || {}).branch_name} · {so.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Timeline */}
                <div>
                  <Label className="flex items-center gap-1 mb-1"><Clock className="w-4 h-4" /> Timeline</Label>
                  <div className="space-y-1.5">
                    {(job.status_history || []).map((h, i) => (
                      <div key={i} className="text-xs text-slate-500 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#1E7A6B]" />
                        <span className="font-medium text-slate-700">{(JOB_STATUS[h.status] || {}).label || h.status}</span>
                        <span className="text-slate-400">{new Date(h.at).toLocaleString()}</span>
                        {h.note && <span className="text-slate-400">· {h.note}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent data-testid="job-complete-dialog">
          <DialogHeader><DialogTitle>Complete job</DialogTitle><DialogDescription>Summarise the work performed on this job.</DialogDescription></DialogHeader>
          <Textarea rows={4} value={workText} onChange={(e) => setWorkText(e.target.value)} placeholder="Describe the work done, parts fitted, tests performed…" data-testid="job-work-performed" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteOpen(false)}>Cancel</Button>
            <Button onClick={complete} disabled={busy} className={`text-white ${accentClass}`} data-testid="confirm-complete-btn"><CheckCircle2 className="w-4 h-4 mr-1" /> Mark completed</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
